import { Input } from '@/components/ui/input.js';
import type { CatalogItem } from '@/lib/api/types.js';
import { cn } from '@/lib/utils.js';
import { useEffect, useRef, useState } from 'react';

type ViewMode = 'grid' | 'list';

export interface CatalogFiltersValue {
  q: string;
  kind: 'report' | 'dashboard' | '';
  category: string;
  favoritesOnly: boolean;
  view: ViewMode;
}

interface CatalogFiltersProps {
  value: CatalogFiltersValue;
  categories: string[];
  onChange: (next: CatalogFiltersValue) => void;
}

export function CatalogFilters({ value, categories, onChange }: CatalogFiltersProps) {
  const [localQ, setLocalQ] = useState(value.q);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setLocalQ(value.q);
  }, [value.q]);

  function handleQChange(e: React.ChangeEvent<HTMLInputElement>) {
    const next = e.target.value;
    setLocalQ(next);
    if (debounceRef.current !== null) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onChange({ ...value, q: next });
    }, 300);
  }

  useEffect(() => {
    return () => {
      if (debounceRef.current !== null) clearTimeout(debounceRef.current);
    };
  }, []);

  function handleKindChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const v = e.target.value as 'report' | 'dashboard' | '';
    onChange({ ...value, kind: v });
  }

  function handleCategoryChange(e: React.ChangeEvent<HTMLSelectElement>) {
    onChange({ ...value, category: e.target.value });
  }

  function handleFavoritesToggle() {
    onChange({ ...value, favoritesOnly: !value.favoritesOnly });
  }

  function handleViewToggle(mode: ViewMode) {
    onChange({ ...value, view: mode });
  }

  const selectClass = cn(
    'h-9 rounded-[var(--radius-input)] border border-[var(--color-qup-border-dark)]',
    'bg-[var(--color-qup-bg-card-dark)] px-2 text-sm text-[var(--color-qup-text-dark)]',
    'focus:outline-none focus:ring-2 focus:ring-[var(--color-qup-primary)]',
    'transition-all duration-200',
  );

  return (
    // biome-ignore lint/a11y/useSemanticElements: <search> is a landmark, not valid here; role="search" on div is intentional
    <div
      role="search"
      aria-label="Filtros del catálogo"
      className="flex flex-wrap items-center gap-3"
    >
      {/* Search */}
      <div className="min-w-[200px] flex-1">
        <Input
          type="search"
          placeholder="Buscar informes…"
          value={localQ}
          onChange={handleQChange}
          aria-label="Buscar informes"
        />
      </div>

      {/* Kind */}
      <select
        value={value.kind}
        onChange={handleKindChange}
        aria-label="Filtrar por tipo"
        className={selectClass}
      >
        <option value="">Todos los tipos</option>
        <option value="report">Informe</option>
        <option value="dashboard">Dashboard</option>
      </select>

      {/* Category */}
      {categories.length > 0 && (
        <select
          value={value.category}
          onChange={handleCategoryChange}
          aria-label="Filtrar por categoría"
          className={selectClass}
        >
          <option value="">Todas las categorías</option>
          {categories.map((cat) => (
            <option key={cat} value={cat}>
              {cat}
            </option>
          ))}
        </select>
      )}

      {/* Favorites only */}
      <button
        type="button"
        role="switch"
        aria-checked={value.favoritesOnly}
        aria-label="Sólo favoritos"
        onClick={handleFavoritesToggle}
        className={cn(
          'flex h-9 items-center gap-2 rounded-[var(--radius-input)] border px-3 text-sm font-medium transition-all duration-200',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-qup-primary)]',
          value.favoritesOnly
            ? 'border-[var(--color-qup-primary)] bg-[var(--color-qup-primary)]/10 text-[var(--color-qup-primary)]'
            : 'border-[var(--color-qup-border-dark)] text-[var(--color-qup-text-muted-dark)] hover:border-[var(--color-qup-primary)]/50',
        )}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill={value.favoritesOnly ? 'var(--color-qup-primary)' : 'none'}
          stroke={value.favoritesOnly ? 'var(--color-qup-primary)' : 'currentColor'}
          strokeWidth="2"
          aria-hidden="true"
        >
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
        Sólo favoritos
      </button>

      {/* View toggle */}
      {/* biome-ignore lint/a11y/useSemanticElements: fieldset styling conflicts with this toggle design */}
      <div
        role="group"
        aria-label="Modo de vista"
        className="flex overflow-hidden rounded-[var(--radius-input)] border border-[var(--color-qup-border-dark)]"
      >
        <button
          type="button"
          aria-pressed={value.view === 'grid'}
          aria-label="Vista cuadrícula"
          title="Vista cuadrícula"
          onClick={() => handleViewToggle('grid')}
          className={cn(
            'flex h-9 w-9 items-center justify-center transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-qup-primary)]',
            value.view === 'grid'
              ? 'bg-[var(--color-qup-primary)] text-white'
              : 'text-[var(--color-qup-text-muted-dark)] hover:text-[var(--color-qup-text-dark)]',
          )}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <rect x="3" y="3" width="7" height="7" />
            <rect x="14" y="3" width="7" height="7" />
            <rect x="3" y="14" width="7" height="7" />
            <rect x="14" y="14" width="7" height="7" />
          </svg>
        </button>
        <button
          type="button"
          aria-pressed={value.view === 'list'}
          aria-label="Vista lista"
          title="Vista lista"
          onClick={() => handleViewToggle('list')}
          className={cn(
            'flex h-9 w-9 items-center justify-center border-l border-[var(--color-qup-border-dark)] transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-qup-primary)]',
            value.view === 'list'
              ? 'bg-[var(--color-qup-primary)] text-white'
              : 'text-[var(--color-qup-text-muted-dark)] hover:text-[var(--color-qup-text-dark)]',
          )}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <line x1="8" y1="6" x2="21" y2="6" />
            <line x1="8" y1="12" x2="21" y2="12" />
            <line x1="8" y1="18" x2="21" y2="18" />
            <line x1="3" y1="6" x2="3.01" y2="6" />
            <line x1="3" y1="12" x2="3.01" y2="12" />
            <line x1="3" y1="18" x2="3.01" y2="18" />
          </svg>
        </button>
      </div>
    </div>
  );
}

// Helper to derive unique category list from items
export function categoriesFromItems(items: ReadonlyArray<CatalogItem>): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const item of items) {
    if (!seen.has(item.category)) {
      seen.add(item.category);
      result.push(item.category);
    }
  }
  return result.sort((a, b) => a.localeCompare(b));
}
